import { dataRegistry } from "./DataRegistry.js";
import {
  DEFAULT_SIGHT,
  DEFAULT_HEARING,
  type MonsterEntity,
  type MonsterAttack,
  type MonsterSize,
  type EnemyFaction,
  type ConditionId,
} from "../types/entity.js";
import type { Position } from "../types/grid.js";
import type { DamageType } from "../types/combat.js";
import type { SRDMonster } from "../types/srd.js";
import { getConditionDef } from "../types/condition.js";
import { parseDiceNotation } from "../combat/dice.js";

/**
 * E17 — resolve a monster's authored stats from `DataRegistry` and build
 * a runtime `MonsterEntity`.
 *
 * Before E17, every enemy/boss/spawner spawn in `main.tsx` hardcoded
 * goblin-tier stats (HP 7, AC 15, CR 1/4) regardless of `monster_id`. A
 * mind flayer authored with HP 71 still entered combat with 7 HP. This
 * helper consults the JSON record and produces an entity with the
 * authored numbers — abilities, damage interactions, spells, loot,
 * reactions, leash radius, faction, sight, hearing, and XP all flow
 * through.
 *
 * When the JSON is missing the helper returns null and the caller falls
 * back to whatever it was doing before (typically a goblin placeholder
 * with a `console.warn`). This keeps test maps that reference fictional
 * monsters from breaking outright.
 */
export interface BuildMonsterArgs {
  readonly instanceId: string;
  readonly monsterId: string;
  readonly position: Position;
  /** Override the JSON's `faction`. Used when Tiled overrides per-spawn. */
  readonly factionOverride?: EnemyFaction;
  /** Override the JSON's `leash_radius`. Used when Tiled overrides per-spawn. */
  readonly leashRadiusOverride?: number;
}

export async function buildMonsterEntity(
  args: BuildMonsterArgs,
): Promise<MonsterEntity | null> {
  const record = await dataRegistry.getMonster(args.monsterId);
  if (!record) return null;
  return buildMonsterEntityFromRecord(record, args);
}

export function buildMonsterEntityFromRecord(
  record: SRDMonster,
  args: BuildMonsterArgs,
): MonsterEntity {
  const faction: EnemyFaction = args.factionOverride ?? record.faction ?? "hostile";
  const leashRadius = args.leashRadiusOverride ?? record.leash_radius ?? 5;

  const abilities = record.ability_scores;
  return {
    instanceId: args.instanceId,
    dataId: record.index,
    kind: "monster",
    name: record.name,
    position: { x: args.position.x, z: args.position.z },
    hp: record.hit_points,
    maxHp: record.hit_points,
    armorClass: record.armor_class,
    challengeRating: record.challenge_rating,
    speed: { walk: record.speed.walk ?? 30 },
    abilities: {
      STR: abilities.strength,
      DEX: abilities.dexterity,
      CON: abilities.constitution,
      INT: abilities.intelligence,
      WIS: abilities.wisdom,
      CHA: abilities.charisma,
    },
    spriteId: args.instanceId,
    faction,
    size: srdSizeToRuntime(record.size),
    anchor: { x: args.position.x, z: args.position.z },
    leashRadius,
    sight: record.sight ?? DEFAULT_SIGHT,
    hearing: record.hearing ?? DEFAULT_HEARING,
    spells: record.spell_ids ?? [],
    reactions: record.reaction_ids ?? [],
    lootTableId: record.loot_table_id ?? null,
    xp: record.xp,
    // EV-E2: authored damage interactions flow into the entity so
    // `applyDamage`'s existing R/V/I math finally sees them.
    ...(record.damage_resistances ? { resistances: record.damage_resistances as DamageType[] } : {}),
    ...(record.damage_vulnerabilities
      ? { vulnerabilities: record.damage_vulnerabilities as DamageType[] }
      : {}),
    ...(record.damage_immunities ? { immunities: record.damage_immunities as DamageType[] } : {}),
    // EV-E1: authored attack block (dice/type/ranged/range/ability).
    ...(() => {
      const attack = parseAttack(record);
      return attack ? { attack } : {};
    })(),
  };
}

/**
 * Parse the record's `attack` block into the runtime shape. Bad dice
 * notation warns and falls back to the legacy melee (attack undefined)
 * rather than breaking the spawn.
 */
function parseAttack(record: SRDMonster): MonsterAttack | undefined {
  const raw = record.attack;
  if (!raw) return undefined;
  try {
    const dice = parseDiceNotation(raw.dice);
    const ranged = raw.ranged === true;
    const rider = raw.condition;
    return {
      diceCount: dice.count,
      diceSides: dice.sides,
      modifier: dice.modifier,
      damageType: raw.type as DamageType,
      ranged,
      range: ranged ? Math.max(1, raw.range ?? 8) : 1,
      ability: raw.ability === "DEX" ? "DEX" : "STR",
      ...(rider && getConditionDef(rider.id as ConditionId)
        ? { conditionOnHit: { id: rider.id as ConditionId, chance: rider.chance } }
        : {}),
    };
  } catch {
    console.warn(`[buildMonsterEntity] ${record.index}: invalid attack dice "${raw.dice}"`);
    return undefined;
  }
}

/**
 * Map SRD's titlecase `size` strings ("Medium") to the runtime three-tier
 * size enum. Anything Tiny/Small collapses to `small`; Large/Huge/Gargantuan
 * collapse to `large`. Unknown values default to `medium`.
 */
function srdSizeToRuntime(size: string): MonsterSize {
  const s = size.toLowerCase();
  if (s === "tiny" || s === "small") return "small";
  if (s === "large" || s === "huge" || s === "gargantuan") return "large";
  return "medium";
}
